import { useEffect, useState } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ExternalLink } from 'lucide-react';

import { Button, Dialog, DialogPortal } from '@/shared';
import { cn } from '@shared/lib';
import { t } from '@/i18n';

import type { FinancialCalendarEvent } from '@/entities/financial-calendar';
import {
  getEventCategoryBadgeClass,
  eventTimeLabel,
  formatEventDateHeading,
  getEventCategoryLabel,
  getCountryFlag,
  getEventTitle,
  getValueTrend,
} from '../model/utils';
import { ImportanceBadge } from './ImportanceBadge';
import { ReleaseBadge } from './ReleaseBadge';
import { formatCalendarValue } from '../model/actual-display';

interface EventDetailDrawerProps {
  event: FinancialCalendarEvent | null;
  onClose: () => void;
}

/** Returns true when viewport is >= 768px (md breakpoint). */
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
};

/**
 * TRAD-2524 — Radix Dialog 전환.
 *
 * 기준 화면: `CalendarFilterDrawer.tsx`(TRAD-2113) — 같은 feature에서 raw backdrop(수동
 * stopPropagation) → Radix Dialog 전환을 이미 거쳤다. 컨테이너/오버레이 껍데기만 Radix로
 * 바꾸고 내부 컨텐츠(배지·값·설명 등)는 한 줄도 안 건드린다(룰 1: 재사용).
 *
 * 🔴 오버레이는 shared `<DialogOverlay />`(bg-black/80)를 그대로 쓰지 않고 `DialogPrimitive.
 * Overlay`를 직접 커스텀한다 — 팀장 조건④(전환 전후 스크림 톤 불변)로 원래 톤(bg-black/40)을
 * 지켜야 하는데, 공용 컴포넌트는 다른 화면(중앙 모달) 기준의 80%를 하드코딩하고 있다.
 */
const DrawerOverlay = () => (
  <DialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-40 bg-black/40',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    )}
  />
);

export const EventDetailDrawer = ({ event, onClose }: EventDetailDrawerProps) => {
  const isDesktop = useIsDesktop();

  /**
   * 🔴 팀장 조건① — 마지막 non-null event 캐시.
   *
   * Radix는 닫힘(data-state=closed) 애니메이션 동안 Content를 즉시 언마운트하지 않고
   * duration만큼 DOM에 유지한다. 그 사이 부모(`pages/financial-calendar/index.tsx`)는
   * `onClose` 호출 즉시 `selectedEvent`를 null로 갱신하므로, 렌더가 `event` prop을 직접 읽으면
   * 퇴장 애니메이션 도중 `event.scheduledAt` 등에서 크래시한다. 그래서 렌더는 항상 "마지막으로
   * 열렸던 event"(`lastEvent`)를 쓰고, 열림 여부만 `event !== null`로 별도 판단한다.
   */
  const [lastEvent, setLastEvent] = useState<FinancialCalendarEvent | null>(null);
  useEffect(() => {
    if (event) setLastEvent(event);
  }, [event]);

  const isOpen = event !== null;
  const displayEvent = event ?? lastEvent;

  // 한 번도 연 적 없으면(=lastEvent도 null) Radix Content 자체가 렌더되지 않으므로 안전하다.
  if (!displayEvent) return null;

  // TRAD-2897 — 목록 카드·상세 패널과 같은 함수(« KST» 는 실제 시각에만).
  const time = eventTimeLabel(displayEvent, { withZone: true });
  // TRAD-2917 — 목록 헤더와 같은 날짜 표기(Intl 경유)로 통일. `KST`/"종일" 시각 표기는
  // formatEventDateHeading이 손대지 않는 별도 영역이라 아래 렌더에서 그대로 이어붙인다.
  const date = formatEventDateHeading(displayEvent.scheduledAt.slice(0, 10));
  // TRAD-2918 — forecast는 정책상(2026-07-30 davidyoon 승인) 무료 API로 못 채워 항상
  // null이다(backend 실측: INDICATOR 33건 전수 forecast 있는 건 0건). hasValues 판정에서도 뺀다.
  const hasValues = displayEvent.actual !== null || displayEvent.previous !== null;
  const trend = getValueTrend(displayEvent.actual, displayEvent.previous);

  const panelClass = cn(
    'fixed z-50 bg-background flex flex-col',
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-200',
    isDesktop
      ? [
          'top-0 right-0 h-screen w-[400px] border-l border-border rounded-l-2xl',
          'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
        ]
      : [
          'bottom-0 left-0 right-0 max-h-[85vh] border-t border-border rounded-t-2xl',
          'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
        ],
  );

  // 팀장 조건② — index.tsx:202의 setTimeout(200)=이 duration-200 커플링을 그대로 유지한다
  // (양쪽 다 건드리지 않음). onOpenChange(false)는 부모의 onClose 그대로 호출 — isClosingRef
  // 가드 로직도 손대지 않는다.
  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogPortal>
        <DrawerOverlay />
        <DialogPrimitive.Content className={panelClass}>
          {/* Drag handle — mobile only */}
          {!isDesktop && (
            <div className="flex-shrink-0 pt-3 pb-1 flex justify-center">
              <div className="h-1 w-12 rounded-full bg-muted" />
            </div>
          )}

          {/* Header */}
          {/* TRAD-2751 — 다이얼로그 타이틀·닫기 aria-label 하드코딩 한글 제거. EventDetailPanel엔
              대응 UI가 없어(패널은 모달이 아님) 신규 키(financial_calendar.detail.title/.close).
              🔴 aria-label은 화면에 안 보이는 속성이라 렌더 텍스트 스캔으로 못 잡는다(2738에서
              같은 유형을 놓친 전례) — 테스트는 getByLabelText로 속성을 직접 검사한다. */}
          <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-border">
            <DialogPrimitive.Title className="text-sm font-semibold">
              {t('financial_calendar.detail.title')}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-auto p-1 text-lg leading-none text-muted-foreground hover:bg-transparent"
                aria-label={t('financial_calendar.detail.close')}
              >
                ✕
              </Button>
            </DialogPrimitive.Close>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 px-5 pb-8 pt-4 space-y-4">
            {/* Badge row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${getEventCategoryBadgeClass(displayEvent.category)}`}
              >
                {getEventCategoryLabel(displayEvent.category)}
              </span>
              <span className="text-sm">{getCountryFlag(displayEvent.country)}</span>
              <ImportanceBadge impact={displayEvent.impact} />
              {/* TRAD-2899 — 상세에도 발표 상태 배지(iOS 와 맞춤). */}
              <ReleaseBadge event={displayEvent} />
            </div>

            {/* Title */}
            <h2 className="font-semibold text-base leading-snug">{getEventTitle(displayEvent)}</h2>

            {/* Date / time */}
            {/* TRAD-2751 — EventDetailPanel과 같은 자리·같은 문구, 같은 키 공유(financial_calendar.event.all_day). */}
            <p className="text-sm text-muted-foreground">
              {date} {time}
            </p>

            {/* Values */}
            {/* TRAD-2918 — forecast 칸 제거(davidyoon 정책 확정 "actual/previous 2칸만",
                designer (가) 채택). 응답 필드(displayEvent.forecast)는 안 지운다 — 유료 API
                전환 시 되돌릴 수 있게 UI만 뺀다. `financial_calendar.values.forecast` 카탈로그
                키는 소비처 0이 되지만 삭제는 TRAD-2930(android 죽은 키와 같은 커밋)에서
                처리 — 이 커밋에서 안 건드린다. */}
            {hasValues && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-3">
                  {t('financial_calendar.values.section')}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm text-center">
                  <div>
                    {/* TRAD-2909/2917 — 옛 `financial_calendar.values.actual`("실제값")에서
                        카드·Panel과 공유하는 `financial_calendar.card.actual`("실제")로 전환.
                        같은 개념이 화면마다 다른 낱말이던 걸 병합(planner 확정, TRAD-2909 §B). */}
                    <p className="text-xs text-muted-foreground mb-1">{t('financial_calendar.card.actual')}</p>
                    {/* 방향은 기호로만. 색 금지 — TRAD-2122 / SoT-17 (근거는 model/utils.ts 주석) */}
                    <p className="font-semibold">
                      {formatCalendarValue(displayEvent, 'actual') ?? '—'}
                      {trend === 'up' && <span className="text-muted-foreground"> ▲</span>}
                      {trend === 'down' && <span className="text-muted-foreground"> ▼</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('financial_calendar.card.previous')}</p>
                    <p>{formatCalendarValue(displayEvent, 'previous') ?? '—'}</p>
                  </div>
                </div>
                {trend && trend !== 'neutral' && (
                  <p className="mt-2 text-xs text-muted-foreground">{t('financial_calendar.values.trend_note')}</p>
                )}
              </div>
            )}

            {/* Description */}
            {/* TRAD-2751 — EventDetailPanel과 같은 자리·같은 문구, 같은 키 공유
                (financial_calendar.detail.description_label / .source_link). */}
            {displayEvent.description && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {t('financial_calendar.detail.description_label')}
                </p>
                <p className="text-sm leading-relaxed text-foreground/80">{displayEvent.description}</p>
              </div>
            )}

            {/* Source URL */}
            {displayEvent.sourceUrl && (
              <a
                href={displayEvent.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-brand-text hover:underline"
              >
                <ExternalLink size={14} />
                {t('financial_calendar.detail.source_link')}
              </a>
            )}

            {/* CoinMarketCal attribution */}
            {displayEvent.category === 'CRYPTO' && (
              <p className="text-2xs text-muted-foreground">Powered by CoinMarketCal</p>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
